import { zxcvbn } from '@zxcvbn-ts/core';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_SCORE_TO_EVAL_SUGGESTIONS,
  PasswordStrengthLevel,
  PasswordStrengthSuggestion,
  WARNING_TO_SUGGESTION_MAP,
} from './types/PasswordStrength.types';

export class PasswordStrengthResult {
  passwordStrengthLevel: PasswordStrengthLevel;
  suggestions: PasswordStrengthSuggestion[];

  constructor(
    passwordStrengthLevel: PasswordStrengthLevel = PasswordStrengthLevel.WEAK,
    suggestions: PasswordStrengthSuggestion[] = [],
  ) {
    this.passwordStrengthLevel = passwordStrengthLevel;
    this.suggestions = suggestions;
  }
}

/**
 * This function computes the password strength using zxcvbn.
 *
 * IMPORTANT: This function assumes that the zxcvbn options have been
 * previously set via the usePasswordDictionaries hook (which loads dictionary
 * data via fetch). If the options are not set, zxcvbn will use its default dictionaries.
 */
function getPasswordEntropy(password: string) {
  return zxcvbn(password);
}

/**
 * Calculates the password strength synchronously.
 *
 * It returns a PasswordStrengthResult based on the computed entropy score.
 * If the password is empty or too short, it returns a weak strength along with
 * an appropriate suggestion.
 */
function calculatePasswordStrength(password: string): PasswordStrengthResult {
  const result = new PasswordStrengthResult(PasswordStrengthLevel.WEAK, []);

  // If no password is provided, return default result with LENGTH suggestion.
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    return { ...result, suggestions: [PasswordStrengthSuggestion.LENGTH] };
  }

  // Calculate password entropy using zxcvbn (which now uses fetched dictionaries if available)
  const entropy = getPasswordEntropy(password);
  const { score } = entropy;

  // we only need to show warnings and suggestions if password is weak
  if (score < PASSWORD_MIN_SCORE_TO_EVAL_SUGGESTIONS && entropy.feedback.warning) {
    const suggestion = WARNING_TO_SUGGESTION_MAP[entropy.feedback.warning];

    // if we match a suggestion, push it to the suggestions for proper validation display in the component
    if (suggestion && !result.suggestions.includes(suggestion)) {
      result.suggestions.push(suggestion);
    }
  }

  // Map the zxcvbn score to a password strength level.
  if (score >= 2 && score <= 3) {
    result.passwordStrengthLevel = PasswordStrengthLevel.MEDIUM;
    result.suggestions.push(PasswordStrengthSuggestion.MEDIUM);
  } else if (score === 4) {
    result.passwordStrengthLevel = PasswordStrengthLevel.STRONG;
    result.suggestions.push(PasswordStrengthSuggestion.STRONG);
  }

  return result;
}

export { calculatePasswordStrength, PasswordStrengthLevel, PasswordStrengthSuggestion };
